import React, { useEffect, useState } from 'react';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import { useCrossOrgEmployees } from '../../hooks/useEmployees';

// Cross-org employee picker — being selected is itself the authorization.
// excludeIds drops people the API would reject (e.g. requester as reviewer).
export default function EmployeeSearchField({
    label,
    required,
    orgId,
    value,
    onSelect,
    onClear,
    placeholder = 'Search by name or email...',
    excludeIds = [],
    error,
    disabled = false,
}) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    const { data: options, isLoading } = useCrossOrgEmployees({
        orgId,
        search: debouncedSearch,
        enabled: dropdownOpen,
    });

    const excluded = excludeIds.filter(Boolean).map(String);
    const employees = (options || []).filter(emp => !excluded.includes(String(emp.id)));

    return (
        <div>
            {label && (
                <label className="block text-sm font-medium mb-5">
                    {label} {required && <span className="text-danger">*</span>}
                </label>
            )}
            <div className="relative">
                <input
                    type="text"
                    disabled={disabled}
                    value={dropdownOpen ? search : (value ? `${value.name}${value.organization ? ` — ${value.organization}` : ''}` : '')}
                    onFocus={() => { setDropdownOpen(true); setSearch(''); }}
                    onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={placeholder}
                    className={`w-full bg-bg-light border rounded-[4px] px-10 pr-30 py-[8px] text-sm focus:outline-none disabled:opacity-60 ${error ? 'border-danger' : 'border-border'}`}
                />
                {value && !disabled && (
                    <button
                        type="button"
                        onClick={() => { onClear?.(); setSearch(''); }}
                        className="absolute right-10 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-main"
                    >
                        <IconX size={14} />
                    </button>
                )}
                {dropdownOpen && (
                    <div className="absolute z-10 top-full left-0 right-0 bg-bg-dark border border-border rounded-[4px] shadow-light mt-2 max-h-[180px] overflow-y-auto">
                        {isLoading ? (
                            <div className="px-12 py-10 text-sm text-text-secondary flex items-center gap-8">
                                <IconLoader2 size={14} className="animate-spin" /> Loading...
                            </div>
                        ) : employees.length === 0 ? (
                            <div className="px-12 py-10 text-sm text-text-secondary">No employees found</div>
                        ) : (
                            employees.map(emp => (
                                <div
                                    key={emp.id}
                                    onMouseDown={() => { onSelect(emp); setDropdownOpen(false); }}
                                    className={`px-12 py-10 cursor-pointer text-sm hover:bg-bg-light transition-colors ${value?.id === emp.id ? 'bg-primary/5 text-primary font-medium' : 'text-text-main'}`}
                                >
                                    <div className="font-medium">{emp.name}</div>
                                    {emp.organization && <div className="text-text-secondary text-xs">{emp.organization}</div>}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
            {error && <p className="text-danger text-xs mt-5">{error}</p>}
        </div>
    );
}
